/**
 * Хук для автоматической подгрузки данных при прокрутке до конца списка
 *
 * Использует IntersectionObserver на невидимом div-триггере.
 * Вызывает fetchNextPage когда триггер становится видимым.
 */

import { useEffect, useRef, type Ref } from 'react';

interface UseInfiniteScrollOptions {
  /** Есть ли следующая страница */
  hasNextPage: boolean;
  /** Идёт ли загрузка следующей страницы */
  isFetchingNextPage: boolean;
  /** Функция подгрузки следующей страницы */
  fetchNextPage: () => void;
  /** Отступ от края для раннего срабатывания (CSS margin) */
  rootMargin?: string;
  /** Включён ли infinite scroll */
  enabled?: boolean;
}

/**
 * Возвращает ref для div-триггера, который нужно поместить в конце списка
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = '200px',
  enabled = true,
}: UseInfiniteScrollOptions): Ref<HTMLDivElement> {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = triggerRef.current;
    if (!element || !enabled || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin, enabled]);

  return triggerRef;
}
